import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter, Route, Switch } from "react-router-dom";
import PropTypes from "prop-types";

import SecureNotesList from "./SecureNotesList";
import RemoveNoteWarning from "./RemoveNoteWarning";
import ShareModal from "../ShowNoteScreen/ShareModal";
import {
  NEW_NOTE_SCREEN,
  SHOW_NOTE_SCREEN,
  PUBLIC_URL
} from "../../constants/Env";
import {
  deleteCurrentNote,
  fetchAllSecureNotes,
  fetchCurrentNote,
  showAssumedUserFailure,
  setScreenToNewNote,
  setScreenToShowNote,
  setScreenToNotesList,
  clearNotesError
} from "../../actions/notesActions";
import SecureNotesDisabled from "./SecureNotesDisabled";

export class SecureNotesScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: ""
    };
  }

  componentDidMount() {
    const {
      assumedBy,
      fetchAllSecureNotes,
      fetchCurrentNote,
      match,
      showAssumedUserFailure,
      setScreenToShowNote,
      setScreenToNewNote,
      setScreenToNotesList
    } = this.props;

    if (!assumedBy) {
      if (match.params.id) {
        // for share and delete modal cases
        setScreenToShowNote();
        fetchCurrentNote(match.params.id);
      } else {
        if (match.url.includes(NEW_NOTE_SCREEN)) {
          setScreenToNewNote();
          document.title = "Secure Notes - New Note"; // Set the title for new notes
        } else if (match.url.includes(SHOW_NOTE_SCREEN)) {
          setScreenToShowNote();
          document.title = "Secure Notes - View/Edit Note"; // Set the title for new notes
        } else {
          setScreenToNotesList();
          document.title = "Secure Notes"; // Default title
        }
      }
      fetchAllSecureNotes();
    } else {
      showAssumedUserFailure();
    }
  }

  openNewNote = () => {
    this.props.setScreenToNewNote();
    this.props.history.push(`${PUBLIC_URL}/new`);
  };

  showSecureNote = id => {
    this.props.fetchCurrentNote(id);
    this.props.setScreenToShowNote();
    this.props.history.push(`${PUBLIC_URL}/${id}`);
  };

  selectFilteredList = searchTerm => {
    const normKeywords = searchTerm.toLowerCase().split(" ");
    return (this.props.notes || []).filter(note =>
      normKeywords.every(word => note.title.toLowerCase().includes(word))
    );
  };

  filterNotes = () => {
    return this.selectFilteredList(this.state.searchTerm);
  };

  onSearch = value => {
    this.setState({
      searchTerm: value
    });
  };

  resetSearch = () => {
    this.setState({
      searchTerm: ""
    });
  };

  onClickRemove = (event, id) => {
    event.stopPropagation();
    this.props.history.push(`${PUBLIC_URL}/delete/${id}`);
  };

  onRemoveSecureNote = id => {
    this.props.deleteCurrentNote(id);
    this.onCloseRemoveModal();
  };

  onCloseRemoveModal = () => {
    this.props.history.push(`${PUBLIC_URL}`);
  };

  onCloseShareModal = () => {
    this.props.history.push(`${PUBLIC_URL}/${this.props.match.params.id}`);
  };

  render() {
    const filteredNotes = this.filterNotes();
    // Dynamically set the title based on the screen
    if (this.props.screen === NEW_NOTE_SCREEN) {
      document.title = "Secure Notes - New Note";
    } else if (this.props.screen === SHOW_NOTE_SCREEN) {
      document.title = "Secure Notes - View/Edit Note";
    } else {
      document.title = "Secure Notes";
    }

    return (
      <>
        {this.props.error ? (
          <SecureNotesDisabled errorMessage={this.props.error.message} />
        ) : (
          <SecureNotesList
            userId={this.props.userId}
            notes={filteredNotes}
            currentNote={this.props.currentNote}
            loading={this.props.loading}
            locale={this.props.locale}
            openNewNote={this.openNewNote}
            showSecureNote={this.showSecureNote}
            onSearch={this.onSearch}
            searchTerm={this.state.searchTerm}
            resetSearch={this.resetSearch}
            onClickRemove={this.onClickRemove}
            showSecureNotePanel={this.props.showSecureNotePanel}
            screen={this.props.screen}
          />
        )}

        <Switch>
          <Route path={`${PUBLIC_URL}/delete/:id`}>
            <RemoveNoteWarning
              onCloseRemoveModal={this.onCloseRemoveModal}
              onRemove={this.onRemoveSecureNote}
            />
          </Route>
          <Route path={`${PUBLIC_URL}/share/:id`}>
            <ShareModal onCloseShareModal={this.onCloseShareModal} />
          </Route>
        </Switch>
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  notes: state.notes.notes,
  currentNote: state.notes.currentNote,
  loading: state.notes.loading,
  locale: state.user.currentLanguageLocale,
  userId: state.user.id,
  assumedBy: state.user.assumedBy,
  showSecureNotePanel: state.notes.showSecureNotePanel,
  screen: state.notes.screen,
  error: state.notes.error
});

const mapDispatchToProps = dispatch => ({
  fetchCurrentNote: id => {
    dispatch(clearNotesError());
    dispatch(fetchCurrentNote(id));
  },
  deleteCurrentNote: id => {
    dispatch(clearNotesError());
    dispatch(deleteCurrentNote(id));
  },
  fetchAllSecureNotes: () => {
    dispatch(clearNotesError());
    dispatch(fetchAllSecureNotes());
  },
  showAssumedUserFailure: () => {
    dispatch(showAssumedUserFailure);
  },
  setScreenToNewNote: () => {
    dispatch(setScreenToNewNote());
  },
  setScreenToShowNote: () => {
    dispatch(setScreenToShowNote());
  },
  setScreenToNotesList: () => {
    dispatch(setScreenToNotesList());
  }
});

SecureNotesScreen.propTypes = {
  notes: PropTypes.array,
  assumedBy: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  showSecureNotePanel: PropTypes.bool.isRequired,
  fetchCurrentNote: PropTypes.func.isRequired,
  deleteCurrentNote: PropTypes.func.isRequired,
  fetchAllSecureNotes: PropTypes.func.isRequired,
  showAssumedUserFailure: PropTypes.func.isRequired,
  setScreenToNewNote: PropTypes.func.isRequired,
  setScreenToShowNote: PropTypes.func.isRequired,
  setScreenToNotesList: PropTypes.func.isRequired
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(SecureNotesScreen)
);
